import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Router } from '@angular/router';
import { RegistrationComponent } from './registration/registration.component';
import { NgxTurnstileModule } from 'ngx-turnstile';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PublicService } from '../../core/services/public.service';
import { interval, map, Subscription, takeWhile } from 'rxjs';
import { AuthService } from '../auth.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-sign-up',
  standalone: true,
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
    CommonModule,
    RegistrationComponent,
    NgxTurnstileModule
  ],
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss']
})
export class SignUpComponent implements OnInit, OnDestroy {

  private snackBar = inject(MatSnackBar);
  private cooldownSubscription: Subscription | null = null;

  mobileNumber: string = '';
  email: string = '';
  token: string = '';
  otp: string = '';
  otpCooldownTime: number = 0;  // Cooldown time in seconds
  otpCooldownTimer: any;
  submitOtpButton: boolean = false;

  showOtpField: boolean = false;
  showRegistrationPage: boolean = false;

  disableProceedButton: boolean = true;
  disableResendButton: boolean = true;

  captchaResponse: string = '';

  // Station selection
  selectedStation: string = '';
  stations = [
    { label: 'Kochi', value: '1' },
    { label: 'Ezhimala', value: '2' },
    { label: 'Lonavala', value: '4' }
  ];

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private publicService: PublicService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    // Remove event listener on component destroy
    if (this.cooldownSubscription) {
      this.cooldownSubscription.unsubscribe();
    }
    window.removeEventListener('beforeunload', this.clearLocalStorage);
  }

  clearLocalStorage(): void {
    // Clear localStorage
    localStorage.clear();
  }

  @HostListener('window:beforeunload', ['$event'])
  handleUnload(event: any) {
    // Retrieve the access token from localStorage
    const token = this.token;
    const url = `${environment.apiUrl}/auth/v1/public/logout?token=${token}`;
    // Send a beacon request
    navigator.sendBeacon(url);  
    // Optional: if you need to clear localStorage
    localStorage.clear();
  }

  sendCaptchaResponse(captchaResponse: any) {
    console.log(`Resolved captcha with response: ${captchaResponse}`);
    if (captchaResponse) {
      this.captchaResponse = captchaResponse;
      this.disableProceedButton = false;
    }
  }

  backToLogin() {
    this.router.navigate(['/app/home'], { replaceUrl: true });
  }

  startOtpCooldown(cooldownTime: number) {

    // Unsubscribe from any existing cooldown timer
    if (this.cooldownSubscription) {
      this.cooldownSubscription.unsubscribe();
    }

    this.disableResendButton = true;
    this.cooldownSubscription = interval(1000).pipe(
      takeWhile(() => cooldownTime >= 0),
      map(() => cooldownTime--)
    ).subscribe({
      next: remainingTime => this.otpCooldownTime = remainingTime,
      complete: () => {
        this.disableResendButton = false;
        this.cooldownSubscription = null; // Reset subscription after completion
      }
    });
  }

  validateMobileNumber(): boolean {
    const mobileRegex = /^[0-9]{10}$/;
    return mobileRegex.test(this.mobileNumber);
  }

  validateEmail(): boolean {
    if (!this.email) return false;
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return emailRegex.test(this.email.trim());
  }

  sendOtp() {
    if (!this.validateMobileNumber()) {
      this.openSnackBar('Mobile Number is not valid', 'Okay');
      return;
    }

    if (!this.selectedStation) {
      this.openSnackBar('Please select a Station', 'Okay');
      return;
    }

    this.publicService.requestOtpForSignUp(this.mobileNumber.trim(), this.captchaResponse, '', this.selectedStation, this.email.trim()).subscribe({
      next: (response) => {
        // console.log('POST response:', response);
        if (response.success) {
          this.showOtpField = true;
          this.submitOtpButton = true;
          this.startOtpCooldown(response.cooldownTime); // Cooldown time from response
          this.cdr.detectChanges();
        } else {
          this.backToLogin();
          this.openSnackBar(response.message, '');
          if (response.message !== 'Could not process your request. Please contact your Complaint Cell I/C.') {
            this.showOtpField = true;
            this.submitOtpButton = true;
            this.startOtpCooldown(response.cooldownTime); // Cooldown time from response
            this.cdr.detectChanges();
          }
        }
      },
      error: (error) => {
        console.error('POST error:', error);
        this.openSnackBar('Something went wrong', 'Okay');
      }
    });
  }

  validateOtpPin(): boolean {
    const mobileRegex = /^[0-9]{6}$/;
    return mobileRegex.test(this.otp);
  }

  validateOtp() {
    if (!this.validateOtpPin()) {
      this.openSnackBar('OTP is not valid', 'Okay');
      return;
    }

    this.authService.validateRequestedOtp({
      userName: 'SIGNUP_USER',
      mobileNumber: this.mobileNumber,
      email: null,
      otp: this.otp,
      source: 'SIGN_UP',
      captchaResponse: null
    }).subscribe({
      next: async (response) => {
        // console.log('POST response:', response);
        if (response.loginSuccess) {
          this.token = response.token;
          this.showRegistrationPage = response.loginSuccess;
          this.cdr.detectChanges();
        } else {
          this.openSnackBar('Invalid or Expired OTP', 'Okay');
        }
      },
      error: (error) => {
        console.error('POST error:', error);
        this.openSnackBar('Username or Mobile Number not valid', 'Okay');
      },
    });
  }

  resendOtp() {
    if (!this.validateMobileNumber()) {
      this.openSnackBar('Mobile Number is not valid.', 'Okay');
      return;
    }

    this.publicService.requestOtpForSignUp(this.mobileNumber.trim(), this.captchaResponse, 'resend', this.selectedStation, this.email.trim()).subscribe({
      next: (response) => {
        // console.log('Resend OTP response:', response);
        if (response.success) {
          this.showOtpField = true;
          this.startOtpCooldown(response.cooldownTime); // Cooldown time from response
          this.cdr.detectChanges();
        } else {
          this.openSnackBar(response.message, 'Okay');
          this.showOtpField = true;
          this.startOtpCooldown(response.cooldownTime); // Cooldown time from response
          this.cdr.detectChanges();
        }
      },
      error: (error) => {
        console.error('POST error:', error);
        this.openSnackBar('Something went wrong', 'Okay');
      }
    });
  }

  openSnackBar(message: string, action?: string) {
    this.snackBar.open(message, action, { duration: 30 * 1000 }); // 30 seconds
  }
}
